import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

/**
 * Lopettia-flavored Button. The default variant uses the teal→teal-dark
 * gradient and flips to orange on hover, with a 1px upward lift — the
 * signature interaction copied verbatim from lorenza.css. Focus rings
 * use the soft 3px teal-glow rather than a hard outline.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[6px] text-sm font-semibold transition-all focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_var(--color-lop-teal-glow)] disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-[linear-gradient(135deg,var(--color-lop-teal),var(--color-lop-teal-dark))] text-[var(--color-lop-bg-deep)] shadow-[var(--shadow-lop-sm)] hover:bg-[linear-gradient(135deg,var(--color-lop-orange),var(--color-lop-orange-dark))] hover:-translate-y-px hover:shadow-[var(--shadow-lop-md)]',
        destructive:
          'bg-[var(--color-lop-danger)] text-white hover:bg-[var(--color-lop-danger)]/90 hover:-translate-y-px',
        outline:
          'border border-[var(--color-lop-border-bright)] bg-transparent text-[var(--color-lop-text-secondary)] hover:border-[var(--color-lop-teal)] hover:bg-[var(--color-lop-bg-card)] hover:text-[var(--color-lop-teal)]',
        secondary:
          'bg-[var(--color-lop-bg-card)] text-[var(--color-lop-text-primary)] border border-[var(--color-lop-border)] hover:bg-[var(--color-lop-bg-card-hi)] hover:border-[var(--color-lop-border-bright)]',
        ghost:
          'text-[var(--color-lop-text-secondary)] hover:bg-[var(--color-lop-bg-card)] hover:text-[var(--color-lop-text-primary)]',
        link: 'text-[var(--color-lop-teal)] underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 px-3 text-[13px]',
        lg: 'h-11 px-8 text-[15px]',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

// eslint-disable-next-line react-refresh/only-export-components
export { buttonVariants };
